import { getLCP, getFID, getCLS } from 'web-vitals'

// Based upon https://web.dev/debug-web-vitals-in-the-field/
function getSelector(node, maxLength = 100) {
  let selector = ''
  let currentNode = node

  try {
    while (currentNode && currentNode.nodeType !== 9) {
      const part = currentNode.id
        ? `#${currentNode.id}`
        : currentNode.nodeName.toLowerCase() +
          (currentNode.className && currentNode.className.length
            ? `.${Array.from(currentNode.classList.values()).join('.')}`
            : '')

      if (selector.length + part.length > maxLength - 1) {
        return selector || part
      }

      selector = selector ? `${part}>${selector}` : part
      if (currentNode.id) {
        break
      }

      currentNode = currentNode.parentNode
    }
  } catch (err) {
    // Do nothing...
  }
  return selector
}

function getLargestLayoutShiftEntry(entries) {
  return entries.reduce((a, b) => (a && a.value > b.value ? a : b))
}

function getLargestLayoutShiftSource(sources) {
  return sources.reduce((a, b) =>
    a.node &&
    a.previousRect.width * a.previousRect.height >
      b.previousRect.width * b.previousRect.height
      ? a
      : b
  )
}

/**
 * Generates additional Google Analytics dimensions for debugging purposes.
 * The dimensions are visible straight in Google Analytics, as well as
 * in the Web Vitals Report tool: https://web.dev/debug-web-vitals-in-the-field/#the-web-vitals-report-tool
 */
function getDebugDimensions(name, entries = []) {
  // In some cases there won't be any entries (e.g. if CLS is 0,
  // or for LCP if you click the “Back” button & the page is restored from the cache),
  // so we have to check first.
  if (entries.length > 0) {
    if (name === 'LCP') {
      const lastEntry = entries[entries.length - 1]
      return {
        // dimension1 is the selector of the element that caused LCP
        dimension1: getSelector(lastEntry.element),
        // dimension2 is when the event happened
        dimension2: String(lastEntry.startTime)
      }
    } else if (name === 'FID') {
      const firstEntry = entries[0]
      return {
        // dimension1 is the selector of the element that caused FID
        dimension1: getSelector(firstEntry.target),
        // dimension2 is when the event happened
        dimension2: String(firstEntry.startTime),
        // dimension3 is what kind of input triggered FID
        dimension3: String(firstEntry.name),
        // dimension4 is whether the input occurred before or after hydration
        dimension4:
          window.coreWebVitals_reactHydratedTime &&
          firstEntry.startTime > window.coreWebVitals_reactHydratedTime
            ? 'post_hydration'
            : 'pre_hydration'
      }
    } else if (name === 'CLS') {
      const largestEntry = getLargestLayoutShiftEntry(entries)
      if (largestEntry && largestEntry.sources && largestEntry.sources.length) {
        const largestSource = getLargestLayoutShiftSource(largestEntry.sources)
        if (largestSource) {
          return {
            // dimension1 is the selector of the element that caused CLS
            dimension1: getSelector(largestSource.node),
            // dimension2 is when the event happened
            dimension2: String(largestEntry.startTime)
          }
        }
      }
    }
  }

  // Return default/empty params in case there are no entries.
  return {
    dimension1: '(not set)'
  }
}

// Based upon https://github.com/GoogleChrome/web-vitals#using-analyticsjs
function sendToGoogleAnalytics({ name, id, delta, entries }) {
  if (!window.ga) {
    return
  }

  // Call the function when GA is ready:
  window.ga(() => {
    console.log('sendToGoogleAnalytics')
    // Get the tracking target, per https://stackoverflow.com/a/51705995/1192426.
    // Otherwise, GA will refuse to track the event.
    const trackingTarget = window.ga.getAll()[0].get('name') // looks like gmt1 or gmt8 or whatever

    // Track the Web Vitals event:
    window.ga(trackingTarget + '.send', 'event', {
      eventCategory: 'Web Vitals',
      eventAction: name,
      // The `id` value will be unique to the current page load. When sending
      // multiple values from the same page (e.g. for CLS), Google Analytics can
      // compute a total by grouping on this ID (note: requires `eventLabel` to
      // be a dimension in your report).
      eventLabel: id,
      // Google Analytics metrics must be integers, so the value is rounded.
      // For CLS the value is first multiplied by 1000 for greater precision
      // (note: increase the multiplier for greater precision if needed).
      eventValue: Math.round(name === 'CLS' ? delta * 1000 : delta),
      // Use a non-interaction event to avoid affecting bounce rate.
      nonInteraction: true,
      // Use `sendBeacon()` if the browser supports it.
      transport: 'beacon',

      ...getDebugDimensions(name, entries)
    })
    console.log('sendToGoogleAnalytics success')
  })
}

function sendWebVitalsToGoogleAnalytics() {
  getCLS(sendToGoogleAnalytics)
  getFID(sendToGoogleAnalytics)
  getLCP(sendToGoogleAnalytics)
}

export default sendWebVitalsToGoogleAnalytics
